// URL Parameter utility functions for Content Planner
// Short format: ?v=status&s=in_progress,approved&b=5&a=3&d=overdue&ct=blog_post&q=search

// URL parameter key mapping (short format)
const PARAM_KEYS = {
  view: 'v',
  status: 's',
  status_not: 's_not',
  brand: 'b',
  brand_not: 'b_not',
  assignee: 'a',
  assignee_not: 'a_not',
  deadline: 'd',
  contentType: 'ct',
  contentType_not: 'ct_not',
  project: 'p',
  project_not: 'p_not',
  search: 'q',
};

// Mapping of filterBy values to URL param keys
const FILTER_BY_TO_PARAM = {
  'state': 'status',
  'brand_id': 'brand',
  'assignee_id': 'assignee',
  'deadline_date': 'deadline',
  'content_type': 'contentType',
  'project_id': 'project',
};

// Reverse mapping
const PARAM_TO_FILTER_BY = Object.entries(FILTER_BY_TO_PARAM).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

/**
 * Encode filter array (ContentPlanner format) to URL search params
 * @param {Array} filtersArray - Array of filter objects from ContentPlanner
 * @param {string} viewMode - Current view mode
 * @param {string} searchValue - Search query
 * @returns {URLSearchParams} - URL search params object
 */
export const encodeFiltersToURL = (filtersArray, viewMode, searchValue) => {
  const params = new URLSearchParams();

  // View mode
  if (viewMode && viewMode !== 'general') {
    params.set(PARAM_KEYS.view, viewMode);
  }

  // Search query
  if (searchValue && searchValue.trim()) {
    params.set(PARAM_KEYS.search, searchValue.trim());
  }

  // Process each filter
  filtersArray.forEach((filter) => {
    const { filterBy, isOptions, filterValue } = filter;

    if (!filterBy || !filterValue) return;

    // Get the param key for this filter type
    const paramType = FILTER_BY_TO_PARAM[filterBy];
    if (!paramType) return;

    // Determine if this is a "not" filter
    const isNot = isOptions === '_not_in';
    const paramKey = PARAM_KEYS[isNot ? `${paramType}_not` : paramType];

    // Convert filterValue to string format
    let valueStr;
    if (Array.isArray(filterValue)) {
      // Extract values from array of objects: [{value: 1, label: ...}, ...]
      const values = filterValue.map(v => v.value || v).filter(Boolean);
      if (values.length === 0) return;
      valueStr = values.join(',');
    } else if (typeof filterValue === 'object' && filterValue.value) {
      valueStr = filterValue.value;
    } else {
      valueStr = String(filterValue);
    }

    if (valueStr) {
      params.set(paramKey, valueStr);
    }
  });

  return params;
};

/**
 * Decode URL search params to ContentPlanner filter array format
 * @param {URLSearchParams} searchParams - URL search params
 * @returns {Object} - { filtersArray, viewMode, searchValue }
 */
export const decodeURLToFilters = (searchParams) => {
  const filtersArray = [];
  let viewMode = 'general';
  let searchValue = '';

  // View mode
  const view = searchParams.get(PARAM_KEYS.view);
  if (view) {
    viewMode = view;
  }

  // Search query
  const search = searchParams.get(PARAM_KEYS.search);
  if (search) {
    searchValue = search;
  }

  // Process each filter type
  Object.entries(PARAM_TO_FILTER_BY).forEach(([paramType, filterBy]) => {
    // Check for both "is" and "is not" versions
    ['', '_not'].forEach((suffix) => {
      const paramKey = PARAM_KEYS[`${paramType}${suffix}`];
      const paramValue = searchParams.get(paramKey);

      if (!paramValue) return;

      const isNot = suffix === '_not';
      const values = paramValue.split(',').filter(Boolean);

      if (values.length === 0) return;

      // Convert values based on type
      let filterValue;
      if (filterBy === 'brand_id' || filterBy === 'assignee_id' || filterBy === 'project_id') {
        // IDs should be numbers, wrapped in {value, label} format for react-select
        filterValue = values.map(v => ({
          value: parseInt(v, 10),
          label: '', // Label will be populated by the component
        }));
      } else if (filterBy === 'deadline_date') {
        // Deadline is a single date value
        filterValue = paramValue;
      } else {
        // Status, content_type are strings
        filterValue = values.map(v => ({
          value: v,
          label: v,
        }));
      }

      filtersArray.push({
        filterBy,
        isOptions: isNot ? '_not_in' : '_in',
        filterValue,
      });
    });
  });

  // If no filters from URL, return default empty filter
  if (filtersArray.length === 0) {
    filtersArray.push({
      filterBy: '',
      isOptions: '_in',
      filterValue: '',
    });
  }

  return { filtersArray, viewMode, searchValue };
};

